import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface KlingEffectsMultiV1Params {
  /**
   * 输入参数
   */
  input: {
    /**
     * 输入图像URL列表，必须包含2个图像
     */
    images: string[];
    /**
     * 视频时长，必须为正数
     */
    duration: number;
  };
  /**
   * 特效场景，可选值：'hug'、'kiss'、'heart_gesture'
   * @values hug | kiss | heart_gesture
   */
  effect_scene: 'hug' | 'kiss' | 'heart_gesture';
  /**
   * 外部任务ID
   */
  external_task_id?: string;
}

export interface KlingEffectsMultiV1Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * kling_effects_multi_v1
 *
 * Kling AI Multi Person Video Effects Generator (V1 Model)
 * 
 * Multi person video effects generation implementation for Kling AI API using V1 model.
 * 
 * Model: kling-v1
 * Features: 多人视频特效 (拥抱/亲吻/比心)
 * Effect Scenes: hug, kiss, heart_gesture
 * Input: 2张图像
 * 
 * API Documentation: https://app.klingai.com/cn/dev/document-api/apiReference/model/videoEffects
 *
 * @example
 * ```typescript
 * import { initSeacloud, klingEffectsMultiV1 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await klingEffectsMultiV1({
 *   // 参数见 KlingEffectsMultiV1Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 KlingEffectsMultiV1Params 接口定义
 * @returns 生成的资源列表
 */
export async function klingEffectsMultiV1(
  params: KlingEffectsMultiV1Params,
): Promise<KlingEffectsMultiV1Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'kling_effects_multi_v1',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: KlingEffectsMultiV1Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
